import { randomUUID as uuidv4 } from 'expo-crypto';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { logCartAddToCart, logCartRemoveFromCart } from 'features/cart/utils/analytics';
import {
  mapChildrenPriceToMenuSelectionsPrice,
  mapMenuSelectionsToICartEntry,
  mapMenuSelectionsToMinimalCartEntry,
  mapSelectedProductToMenuSelections,
  normalizeIncentiveComboEntry,
} from 'features/cart/utils/minimal-cart';
import { SlotsSelections } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useLoyaltyOffersMap } from 'features/offer/hooks/use-loyalty-offers-map';
import { useStoreContext } from 'features/store';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';

import { toaster } from 'components/toaster/toaster';
import { useCart } from 'hooks/use-cart';
import { isCartEntryReorderable } from 'hooks/use-reorderable-orders/utils';
import { getStaticPathname } from 'navigation/location/location';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import {
  getCartIdToEvictForNewRewardSlot,
  sumAppliedTimesApplied,
} from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useRewardRedemptionLimit } from 'state/loyalty/hooks/use-reward-redemption-limit';
import { LoyaltyAppliedOffer } from 'state/loyalty/types';
import { getServiceModeFromLS, useServiceModeLive } from 'state/service-mode';
import { useSmartAlertsContext } from 'state/smart-alert';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import {
  isCombo,
  isStandaloneOrderableMenuEntry,
  mapImageResourceToSanityImage,
} from 'state/unified-menu/utils';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

export const resolveCartEntryUrl = (
  selectedProduct: Pick<ILocalizedMenuEntry, 'id' | 'type'>,
  url?: string
): string => {
  if (url) {
    return url;
  }

  const pathname = getStaticPathname();
  if (pathname.startsWith(`${routes.menu}/`)) {
    return pathname;
  }

  return `${routes.menu}/${selectedProduct.type.toLowerCase()}/${selectedProduct.id}`;
};

const getAddToCartSuccessMessage = (
  entry: { name: string; quantity: number },
  isEditing: boolean
): string => {
  if (isEditing) {
    return t('updateCartSuccess', { itemName: entry.name });
  }
  if (entry.quantity > 1) {
    return t('addToCartMultipleSuccess', {
      itemName: entry.name,
      quantity: entry.quantity - 1,
    });
  }
  return t('addToCartSuccess', { itemName: entry.name });
};

const getOfferIdFromUrl = (url?: string) => {
  if (!url) {
    return undefined;
  }

  const match = url.match(/[?&]offerId=([^&]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
};

const getOfferComboBenefitId = ({
  indexedOffers,
  indexedMenu,
  offerWrapperId,
}: {
  indexedOffers: ReturnType<typeof useUnifiedMenuContext>['indexedOffers'];
  indexedMenu: ReturnType<typeof useUnifiedMenuContext>['indexedMenu'];
  offerWrapperId?: string;
}) => {
  if (!offerWrapperId) {
    return undefined;
  }

  const offerEntry = indexedOffers.find(offerWrapperId);
  const benefitId = offerEntry?.options?.[0]?.entryId;
  if (!benefitId) {
    return undefined;
  }

  const benefitMenuEntry = indexedMenu.find(benefitId);
  return benefitMenuEntry && isCombo(benefitMenuEntry) ? benefitId : undefined;
};

export const useAddToCart = () => {
  const dispatch = useAppDispatch();
  const { indexedMenu, indexedOffers, indexedRewards } = useUnifiedMenuContext();
  const {
    addItemToMinimalCart,
    updateItemInMinimalCart,
    hydrateMinimalCart,
    removeItemFromMinimalCart,
    cartEntries,
  } = useMinimalCart();
  const { posServiceMode } = useMenuQueryOptions();
  const { cartIdEditing, rewardBenefitId, rewardId, offerId } = useProductWizardFlow();
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const { isStoreAvailable } = useStoreAvailabilityFns();
  const { currentStoreDate } = useSmartAlertsContext();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const { removeFromCart } = useCart();
  const { editFavoriteOneMenuAdapter, tempFavorite: currentFavorite } = useFavoritesContext();
  const loyaltyOffersMap = useLoyaltyOffersMap();
  const { enabled: isLoyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const enableMinimalCartUi = useMinimalCartUi();

  /**
   * @deprecated
   * this function is used to add items to the legacy cart, it supports some of the hydration logic
   * for the legacy cart.
   * We should migrate this function to use the minimal cart.
   * This function will be removed in the future.
   */
  const addToLegacyCart = async (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections
  ) => {
    if (!isStoreAvailable(store, serviceMode, currentStoreDate ?? store.localTime)) {
      return toaster.negative(t('restaurantUnavailableErrorTitle'));
    }

    const cartEntryUrl = resolveCartEntryUrl(selectedProduct);
    const menuSelections = mapSelectedProductToMenuSelections(
      selectedProduct,
      quantity,
      slotsSelections
    );

    // If there is a favorite active in the context, add to the favorite instead of the cart
    if (currentFavorite) {
      const [mappedEntry] = mapMenuSelectionsToMinimalCartEntry(menuSelections, {
        menu: indexedMenu,
        indexedOffers,
        posServiceMode,
        indexedRewards,
        url: cartEntryUrl,
        offerId,
      });

      // When editing a favorite, ensure that the cartId is preserved so that upsertEntry can find the existing entry
      const favoriteEntryWithCartId = {
        ...mappedEntry,
        cartId: cartIdEditing || mappedEntry.cartId || uuidv4(),
      };

      return editFavoriteOneMenuAdapter({
        minimalCartEntries: [favoriteEntryWithCartId],
      });
    }

    const rewardBenefitSelection = menuSelections[0];

    const [mappedEntry] = mapMenuSelectionsToMinimalCartEntry(
      rewardId ? [rewardBenefitSelection] : menuSelections,
      {
        menu: indexedMenu,
        indexedOffers,
        indexedRewards,
        posServiceMode,
        url: cartEntryUrl,
        rewardId,
        offerId,
      }
    );

    const isRewardEntry = !cartIdEditing && rewardBenefitId;

    const minimalCartEntry = {
      ...mappedEntry,
      cartId: cartIdEditing || uuidv4(),
      ...(cartIdEditing && { isEdit: true }),
      ...(isRewardEntry && { rewardBenefitId, rewardId }),
    };

    const evictCartId =
      enableRewardUnstacking &&
      isRewardEntry &&
      getCartIdToEvictForNewRewardSlot(
        appliedLoyaltyRewards,
        minimalCartEntry.cartId ?? '',
        rewardRedemptionLimit
      );

    // When evicting an existing reward, the legacy `removeFromCart` only updates the legacy cart
    // state — it does not dispatch `removeAppliedReward` to Redux. As a result, the
    // `totalTimesRewardApplied` selector inside `hydrateMinimalCart` still counts the evicted
    // reward, which causes `availableRewardSlots` to be computed as 0. Passing
    // `rewardSlotsFreedByEviction` lets `hydrateMinimalCart` correct for this so that
    // `handleRewards` is not skipped for the incoming swap reward.
    const rewardSlotsFreedByEviction = evictCartId
      ? appliedLoyaltyRewards[evictCartId]?.timesApplied ?? 0
      : 0;

    if (evictCartId) {
      removeFromCart({ cartId: evictCartId });
    }

    const isHydrationSuccessful = await hydrateMinimalCart([minimalCartEntry], {
      rewardSlotsFreedByEviction,
    });
    if (!isHydrationSuccessful) {
      return toaster.negative(t('addToCartError', { itemName: minimalCartEntry.name }));
    }

    if (selectedLoyaltyOffer?._id === minimalCartEntry.id) {
      if (!selectedLoyaltyOffer.isStackable) {
        appliedOffers.forEach(offer => {
          if (!offer.isStackable && !!offer.cartId) {
            removeFromCart({ cartId: offer.cartId });
          }
        });
      }
      dispatch(
        actions.loyalty.applyOffer({
          id: selectedLoyaltyOffer.loyaltyEngineId,
          type: (selectedLoyaltyOffer as LoyaltyAppliedOffer).type,
          cartId: minimalCartEntry.cartId,
          isStackable: selectedLoyaltyOffer.isStackable,
          cmsId: selectedLoyaltyOffer._id,
        })
      );
      dispatch(actions.loyalty.setSelectedOffer(null));
    }

    const message = getAddToCartSuccessMessage(minimalCartEntry, !!cartIdEditing);

    return toaster.addToCartToast(
      message,
      rewardId
        ? mapImageResourceToSanityImage(
            selectedProduct.image?.resource ?? '',
            selectedProduct.image?.altText?.locale ?? ''
          )
        : minimalCartEntry.image
    );
  };

  const buildMenuSelections = (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections
  ) => {
    let menuSelections = mapSelectedProductToMenuSelections(
      selectedProduct,
      quantity,
      slotsSelections
    );

    if (rewardId) {
      menuSelections = [
        {
          id: rewardId,
          quantity, // Check the stacking logic here?
          menuSelections,
        },
      ];
    }

    return menuSelections;
  };

  const buildMappedEntryWithPrices = (
    menuSelections: ReturnType<typeof buildMenuSelections>,
    url?: string
  ) => {
    const [mappedEntry]: ICartEntry[] = mapMenuSelectionsToICartEntry(menuSelections, {
      menu: indexedMenu,
      posServiceMode,
      url: url ?? '',
      indexedOffers,
      indexedRewards,
      rewardId,
    });

    // The mapped entry has price at children level, we have to add the price to the menu selections tree
    const menuSelectionsWithPrice = mapChildrenPriceToMenuSelectionsPrice(mappedEntry);
    // mappedEntry.menuSelections is readonly, so create a new object with updated menuSelections
    return {
      ...mappedEntry,
      price: {
        amount:
          mappedEntry.type === MinimalCartEntryType.reward
            ? (mappedEntry.price?.amount ?? 0) * -1
            : mappedEntry.price?.amount ?? 0,
        currency: 'USD',
      },
      menuSelections: menuSelectionsWithPrice,
    };
  };

  const maybeAddFavoriteEntry = (mappedEntryWithPrices: ICartEntry): boolean => {
    if (!currentFavorite) {
      return false;
    }

    // When editing a favorite, ensure that the cartId is preserved so that upsertEntry can find the existing entry
    const favoriteEntryWithCartId = {
      ...mappedEntryWithPrices,
      cartId: cartIdEditing || mappedEntryWithPrices.cartId || uuidv4(),
    };
    editFavoriteOneMenuAdapter({
      minimalCartEntries: [favoriteEntryWithCartId],
    });
    return true;
  };

  const buildMinimalCartEntry = (mappedEntryWithPrices: ICartEntry) => ({
    ...mappedEntryWithPrices,
    ...(cartIdEditing && { isEdit: true }),
    cartId: cartIdEditing || uuidv4(),
  });

  const validateIncentiveEntry = <T extends ICartEntry>(minimalCartEntry: T): T => {
    if (minimalCartEntry.type !== MinimalCartEntryType.incentive) {
      return minimalCartEntry;
    }

    const offerEntry = loyaltyOffersMap[minimalCartEntry.id];
    if (!offerEntry && (!isLoyaltyServiceOffersEnabled || !minimalCartEntry.referenceId)) {
      // When enabled, the map is applied-only, so a miss is expected — but a
      // `referenceId`-less entry is genuinely unresolvable and invisible to the
      // applied-offers batch/eviction, so it still warns.
      logger.warn({
        message: '[useAddToCart] offerEntry not found',
        context: { minimalCartEntry },
      });
    }
    if (offerEntry && offerEntry.loyaltyEngineId !== minimalCartEntry.referenceId) {
      logger.warn({
        message:
          '[useAddToCart] loyaltyEngineId from oneMenu differs from the one retrieved from loyalty',
        context: {
          loyaltyOfferEntry: offerEntry,
          minimalCartEntry,
        },
      });
      return {
        ...minimalCartEntry,
        referenceId: offerEntry.loyaltyEngineId,
      } as T;
    }

    return minimalCartEntry;
  };

  const removeAppliedRewardIfNeeded = (minimalCartEntry: ICartEntry) => {
    const evictCartId =
      enableRewardUnstacking &&
      rewardId &&
      !cartIdEditing &&
      minimalCartEntry.type === MinimalCartEntryType.reward &&
      getCartIdToEvictForNewRewardSlot(
        appliedLoyaltyRewards,
        minimalCartEntry.cartId ?? '',
        rewardRedemptionLimit
      );
    if (evictCartId) {
      const cartEntryToRemove = cartEntries.find(entry => entry.cartId === evictCartId);
      if (!cartEntryToRemove) {
        return;
      }
      removeItemFromMinimalCart(cartEntryToRemove);
    }
  };

  const applyRewardIfNeeded = (minimalCartEntry: ICartEntry, rewardCartIds: string[]) => {
    if (!rewardId || !!cartIdEditing || minimalCartEntry.type !== MinimalCartEntryType.reward) {
      return;
    }
    for (const cartId of rewardCartIds) {
      dispatch(
        actions.loyalty.applyReward({
          cartId,
          rewardBenefitId: rewardBenefitId ?? '',
          quantity: 1,
          redemptionMethod: RewardRedemptionMethod.MOBILE,
          enableRewardUnstacking,
          rewardRedemptionLimit,
        })
      );
    }
  };

  const persistMinimalCartEntry = (minimalCartEntry: ICartEntry, rewardCartIds: string[]) => {
    if (cartIdEditing) {
      if (minimalCartEntry.quantity > 1) {
        updateItemInMinimalCart(cartIdEditing, { ...minimalCartEntry, quantity: 1 });
        for (let i = 0; i < minimalCartEntry.quantity - 1; i++) {
          const entryCopy = { ...minimalCartEntry, quantity: 1, cartId: uuidv4() };
          addItemToMinimalCart(entryCopy);
        }
      } else {
        updateItemInMinimalCart(cartIdEditing, minimalCartEntry);
      }
    } else {
      if (minimalCartEntry.quantity > 1) {
        if (enableRewardUnstacking && minimalCartEntry.type === MinimalCartEntryType.reward) {
          // Add one reward entry per pre-computed cartId; remaining units become regular items.
          for (const cartId of rewardCartIds) {
            addItemToMinimalCart({
              ...minimalCartEntry,
              quantity: 1,
              cartId,
              menuSelections: [
                {
                  id: minimalCartEntry.id,
                  quantity: 1,
                  menuSelections: minimalCartEntry.menuSelections,
                },
              ],
              children: [
                {
                  ...minimalCartEntry.children[0],
                  quantity: 1,
                },
              ],
            });
          }
          for (let i = rewardCartIds.length; i < minimalCartEntry.quantity; i++) {
            const childEntry = minimalCartEntry.children?.[0];
            if (!childEntry) {
              continue;
            }
            addItemToMinimalCart({ ...childEntry, quantity: 1, cartId: uuidv4() });
          }
        } else {
          for (let i = 0; i < minimalCartEntry.quantity; i++) {
            const entryCopy = { ...minimalCartEntry, quantity: 1, cartId: uuidv4() };
            addItemToMinimalCart(entryCopy);
          }
        }
      } else {
        addItemToMinimalCart(minimalCartEntry);
      }
    }
  };

  const showSuccessToast = (
    minimalCartEntry: ICartEntry,
    selectedProduct: ILocalizedMenuEntry,
    onRootToaster = true
  ) => {
    const message = getAddToCartSuccessMessage(minimalCartEntry, !!cartIdEditing);

    return toaster.addToCartToast(
      message,
      rewardId
        ? mapImageResourceToSanityImage(
            selectedProduct.image?.resource ?? '',
            selectedProduct.image?.altText?.locale ?? ''
          )
        : minimalCartEntry.image,
      onRootToaster
    );
  };

  /**
   * This function is used to add items to the minimal cart,
   * it follows one menu logic and add items using a hierarchy
   */
  const addToCart = (
    selectedProduct: ILocalizedMenuEntry,
    quantity: number,
    slotsSelections: SlotsSelections,
    url?: string,
    options?: {
      isUpsell?: boolean;
      onRootToaster?: boolean;
    }
  ) => {
    if (!isStoreAvailable(store, serviceMode, currentStoreDate ?? store.localTime)) {
      return toaster.negative(t('restaurantUnavailableErrorTitle'));
    }

    if (!isStandaloneOrderableMenuEntry(selectedProduct, posServiceMode)) {
      logger.warn({
        message: '[useAddToCart] blocked non-standalone One Menu cart entry',
        context: {
          id: selectedProduct.id,
          name: selectedProduct.name?.locale,
          type: selectedProduct.type,
          posServiceMode,
        },
      });
      return false;
    }

    const cartEntryUrl = resolveCartEntryUrl(selectedProduct, url);
    const menuSelections = buildMenuSelections(selectedProduct, quantity, slotsSelections);
    const menuSelectionsWithPrices = buildMappedEntryWithPrices(menuSelections, cartEntryUrl);
    const offerComboBenefitId = getOfferComboBenefitId({
      indexedOffers,
      indexedMenu,
      offerWrapperId: offerId,
    });

    // If there is a favorite active in the context, add to the favorite instead of the cart
    if (maybeAddFavoriteEntry(menuSelectionsWithPrices)) {
      return;
    }

    let minimalCartEntry = buildMinimalCartEntry(menuSelectionsWithPrices);
    minimalCartEntry = {
      ...minimalCartEntry,
      ...(options?.isUpsell ? { isUpsell: true } : {}),
    };
    minimalCartEntry = normalizeIncentiveComboEntry(
      minimalCartEntry,
      !!offerComboBenefitId,
      offerId,
      offerComboBenefitId
    );
    minimalCartEntry = validateIncentiveEntry(minimalCartEntry);
    removeAppliedRewardIfNeeded(minimalCartEntry);

    const availableRewardSlots = Math.max(
      0,
      rewardRedemptionLimit - sumAppliedTimesApplied(appliedLoyaltyRewards)
    );
    const rewardQuantityToApply =
      enableRewardUnstacking &&
      !cartIdEditing &&
      minimalCartEntry.type === MinimalCartEntryType.reward
        ? Math.max(1, Math.min(availableRewardSlots, minimalCartEntry.quantity))
        : 1;
    const rewardCartIds: string[] =
      rewardQuantityToApply > 1
        ? [
            minimalCartEntry.cartId ?? uuidv4(),
            ...Array.from({ length: rewardQuantityToApply - 1 }, () => uuidv4()),
          ]
        : [minimalCartEntry.cartId ?? uuidv4()];

    applyRewardIfNeeded(minimalCartEntry, rewardCartIds);

    const originalEntry = cartIdEditing
      ? cartEntries.find(entry => entry.cartId === cartIdEditing)
      : undefined;

    if (serviceMode) {
      if (originalEntry) {
        logCartRemoveFromCart({
          cartEntry: originalEntry,
          previousCartEntries: [originalEntry],
          serviceMode,
          isReward: originalEntry.type === MinimalCartEntryType.reward,
        });
        logCartAddToCart({
          cartEntry: minimalCartEntry,
          previousCartEntries: [originalEntry],
          serviceMode,
        });
      } else {
        logCartAddToCart({
          cartEntry: minimalCartEntry,
          previousCartEntries: cartEntries,
          serviceMode,
        });
      }
    }

    persistMinimalCartEntry(minimalCartEntry, rewardCartIds);

    return showSuccessToast(minimalCartEntry, selectedProduct, options?.onRootToaster);
  };

  return enableMinimalCartUi ? addToCart : addToLegacyCart;
};

/**
 * Adds pre-mapped minimal entries while preserving add-to-cart semantics
 * (quantity expansion, reward handling, offer reference validation).
 */
type AddMappedEntryToCartOptions = {
  skipSuccessToast?: boolean;
};

export const useAddMappedEntryToCart = () => {
  const dispatch = useAppDispatch();
  const { indexedMenu, indexedOffers } = useUnifiedMenuContext();
  const {
    addItemToMinimalCart,
    updateItemInMinimalCart,
    removeItemFromMinimalCart,
    cartEntries,
  } = useMinimalCart();
  const { cartIdEditing, rewardBenefitId, rewardId } = useProductWizardFlow();
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const rewardRedemptionLimit = useRewardRedemptionLimit();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const loyaltyOffersMap = useLoyaltyOffersMap();
  const { enabled: isLoyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();
  const validateIncentiveEntry = <T extends ICartEntry>(minimalCartEntry: T): T => {
    if (minimalCartEntry.type !== MinimalCartEntryType.incentive) {
      return minimalCartEntry;
    }

    const offerEntry = loyaltyOffersMap[minimalCartEntry.id];
    if (!offerEntry) {
      if (!isLoyaltyServiceOffersEnabled || !minimalCartEntry.referenceId) {
        // See `useAddToCart`: a `referenceId`-less entry is genuinely
        // unresolvable and still warns even when the applied-only miss is expected.
        logger.warn({
          message: '[useAddMappedEntryToCart] offerEntry not found',
          context: { minimalCartEntry },
        });
      }
      return minimalCartEntry;
    }

    if (offerEntry.loyaltyEngineId !== minimalCartEntry.referenceId) {
      return {
        ...minimalCartEntry,
        referenceId: offerEntry.loyaltyEngineId,
      } as T;
    }

    return minimalCartEntry;
  };

  const removeAppliedRewardIfNeeded = (minimalCartEntry: ICartEntry) => {
    const evictCartId =
      enableRewardUnstacking &&
      rewardId &&
      !cartIdEditing &&
      minimalCartEntry.type === MinimalCartEntryType.reward &&
      getCartIdToEvictForNewRewardSlot(
        appliedLoyaltyRewards,
        minimalCartEntry.cartId ?? '',
        rewardRedemptionLimit
      );
    if (evictCartId) {
      const cartEntryToRemove = cartEntries.find(entry => entry.cartId === evictCartId);
      if (cartEntryToRemove) {
        removeItemFromMinimalCart(cartEntryToRemove);
      }
    }
  };

  const applyRewardIfNeeded = (minimalCartEntry: ICartEntry, rewardCartIds: string[]) => {
    if (!rewardId || cartIdEditing || minimalCartEntry.type !== MinimalCartEntryType.reward) {
      return;
    }
    for (const cartId of rewardCartIds) {
      dispatch(
        actions.loyalty.applyReward({
          cartId,
          rewardBenefitId: rewardBenefitId ?? '',
          quantity: 1,
          redemptionMethod: RewardRedemptionMethod.MOBILE,
          enableRewardUnstacking,
          rewardRedemptionLimit,
        })
      );
    }
  };

  const persistMinimalCartEntry = (minimalCartEntry: ICartEntry, rewardCartIds: string[]) => {
    if (cartIdEditing) {
      if (minimalCartEntry.quantity > 1) {
        updateItemInMinimalCart(cartIdEditing, { ...minimalCartEntry, quantity: 1 });
        for (let i = 0; i < minimalCartEntry.quantity - 1; i++) {
          const entryCopy = { ...minimalCartEntry, quantity: 1, cartId: uuidv4() };
          addItemToMinimalCart(entryCopy);
        }
      } else {
        updateItemInMinimalCart(cartIdEditing, minimalCartEntry);
      }
      return;
    }

    if (minimalCartEntry.quantity > 1) {
      if (enableRewardUnstacking && minimalCartEntry.type === MinimalCartEntryType.reward) {
        // Add one reward entry per pre-computed cartId; remaining units become regular items.
        for (const cartId of rewardCartIds) {
          addItemToMinimalCart({
            ...minimalCartEntry,
            quantity: 1,
            cartId,
            menuSelections: [
              {
                id: minimalCartEntry.id,
                quantity: 1,
                menuSelections: minimalCartEntry.menuSelections,
              },
            ],
            children: [
              {
                ...minimalCartEntry.children[0],
                quantity: 1,
              },
            ],
          });
        }
        for (let i = rewardCartIds.length; i < minimalCartEntry.quantity; i++) {
          const childEntry = minimalCartEntry.children?.[0];
          if (!childEntry) {
            continue;
          }
          addItemToMinimalCart({ ...childEntry, quantity: 1, cartId: uuidv4() });
        }
      } else {
        for (let i = 0; i < minimalCartEntry.quantity; i++) {
          const entryCopy = { ...minimalCartEntry, quantity: 1, cartId: uuidv4() };
          addItemToMinimalCart(entryCopy);
        }
      }
    } else {
      addItemToMinimalCart(minimalCartEntry);
    }
  };

  const showSuccessToast = (minimalCartEntry: ICartEntry) => {
    const message = getAddToCartSuccessMessage(minimalCartEntry, !!cartIdEditing);

    return toaster.addToCartToast(message, minimalCartEntry.image);
  };

  return (entry: ICartEntry, options?: AddMappedEntryToCartOptions): boolean => {
    if (!isCartEntryReorderable(entry)) {
      logger.warn({
        message: '[useAddMappedEntryToCart] blocked non-reorderable One Menu cart entry',
        context: {
          id: entry.id,
          name: entry.name,
          type: entry.type,
        },
      });
      return false;
    }

    let minimalCartEntry = {
      ...entry,
      ...(cartIdEditing && { isEdit: true }),
      cartId: cartIdEditing || entry.cartId || uuidv4(),
    };
    const mappedOfferId = getOfferIdFromUrl(minimalCartEntry.url);
    const offerWrapperId =
      mappedOfferId ??
      (minimalCartEntry.type === MinimalCartEntryType.incentive ? minimalCartEntry.id : undefined);
    const offerComboBenefitId = getOfferComboBenefitId({
      indexedOffers,
      indexedMenu,
      offerWrapperId,
    });

    minimalCartEntry = normalizeIncentiveComboEntry(
      minimalCartEntry,
      !!offerComboBenefitId,
      offerWrapperId,
      offerComboBenefitId
    );
    minimalCartEntry = validateIncentiveEntry(minimalCartEntry);
    removeAppliedRewardIfNeeded(minimalCartEntry);

    const availableRewardSlots = Math.max(
      0,
      rewardRedemptionLimit - sumAppliedTimesApplied(appliedLoyaltyRewards)
    );
    const rewardQuantityToApply =
      enableRewardUnstacking &&
      !cartIdEditing &&
      minimalCartEntry.type === MinimalCartEntryType.reward
        ? Math.max(1, Math.min(availableRewardSlots, minimalCartEntry.quantity))
        : 1;
    const rewardCartIds: string[] =
      rewardQuantityToApply > 1
        ? [
            minimalCartEntry.cartId ?? uuidv4(),
            ...Array.from({ length: rewardQuantityToApply - 1 }, () => uuidv4()),
          ]
        : [minimalCartEntry.cartId ?? uuidv4()];

    applyRewardIfNeeded(minimalCartEntry, rewardCartIds);

    const originalEntry = cartIdEditing
      ? cartEntries.find(existingEntry => existingEntry.cartId === cartIdEditing)
      : undefined;

    const serviceMode = getServiceModeFromLS();
    if (serviceMode) {
      if (originalEntry) {
        logCartRemoveFromCart({
          cartEntry: originalEntry,
          previousCartEntries: [originalEntry],
          serviceMode,
          isReward: originalEntry.type === MinimalCartEntryType.reward,
        });
        logCartAddToCart({
          cartEntry: minimalCartEntry,
          previousCartEntries: [originalEntry],
          serviceMode,
        });
      } else {
        logCartAddToCart({
          cartEntry: minimalCartEntry,
          previousCartEntries: cartEntries,
          serviceMode,
        });
      }
    }

    persistMinimalCartEntry(minimalCartEntry, rewardCartIds);

    if (options?.skipSuccessToast) {
      return true;
    }

    showSuccessToast(minimalCartEntry);
    return true;
  };
};
